import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import config from '@/config';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import ChannelsService from '@/services/channels.service';
import fetchWrapper from '@/services/fetchWrapper.service';
import UserService from '@/services/user.service';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import BackArrowIcon from '@/shared-components/icons/components/public/BackArrowIcon';
import Loader from '@/shared-components/loader/Loader';
import Utils from '@/utils';

import RecommendedChannelsSection from './components/RecommendedChannelsSection';
import { resolveChannelOwnerProfileType, resolveImageId } from './utils/channelMessageHelpers';

import './styles/ChannelMenu.css';

function normalizeChannelId(raw) {
  if (!raw) return null;
  if (typeof raw === 'string' || typeof raw === 'number') {
    return String(raw);
  }
  if (typeof raw === 'object') {
    if (raw.$oid) {
      return String(raw.$oid);
    }
    if (raw._id) {
      const nested = normalizeChannelId(raw._id);
      if (nested) return nested;
    }
    if (raw.id) {
      const nested = normalizeChannelId(raw.id);
      if (nested) return nested;
    }
    if (raw.channelId) {
      const nested = normalizeChannelId(raw.channelId);
      if (nested) return nested;
    }
    if (raw.channel_id) {
      const nested = normalizeChannelId(raw.channel_id);
      if (nested) return nested;
    }
  }
  return null;
}

function computeChannelSignature(channels) {
  if (!Array.isArray(channels)) return '';
  return channels
    .map((channel = {}) => {
      const identifier =
        normalizeChannelId(channel._id)
        || normalizeChannelId(channel.id)
        || normalizeChannelId(channel.channelId)
        || normalizeChannelId(channel.channel_id)
        || '';
      const latestMessage =
        channel.latestMessage
        || channel.lastMessage
        || channel.last_message
        || null;
      const lastActivity =
        channel.lastActivity
        || channel.lastActivityAt
        || channel.updatedAt
        || channel.updated_at
        || channel.createdAt
        || '';
      const latestMessageTime =
        latestMessage?.createdAt
        || latestMessage?.created_at
        || latestMessage?.timestamp
        || '';
      const unread =
        channel.unreadCount
        ?? channel.unread
        ?? channel.unreadMessages
        ?? channel.unread_count
        ?? '';
      const title = channel.title || channel.name || '';
      return [identifier, lastActivity, latestMessageTime, unread, title].join('|');
    })
    .join(';');
}

function resolveOwnerAvatar(source) {
  if (!source || typeof source !== 'object') {
    return null;
  }

  const ownerImage =
    resolveImageId(source?.channel?.owner?.user_image)
    || resolveImageId(source?.owner?.user_image)
    || resolveImageId(source?.ownerImage)
    || resolveImageId(source?.owner_image)
    || resolveImageId(source?.avatar)
    || null;

  if (!ownerImage) {
    return null;
  }

  const coverImage =
    resolveImageId(source?.channel?.image)
    || resolveImageId(source?.image)
    || resolveImageId(source?.channelImage)
    || null;

  if (coverImage && ownerImage === coverImage) {
    return null;
  }

  return ownerImage;
}

export default function ChannelMenu({ messagesType, setMessagesType, memberChannels = [], memberLoading = false }) {
  const t = useTranslations('common.profile.channels');
  const { state, stateHandler } = useStateHandler();
  const stateHandlerRef = useRef(stateHandler);
  useEffect(() => {
    stateHandlerRef.current = stateHandler;
  }, [stateHandler]);

  const setChannelUnreadUpdated = useCallback((value) => {
    const handler = stateHandlerRef.current;
    if (!handler) return;
    handler.set('channelUnreadUpdated', value);
  }, []);

  const profileName = state.user_session?.user_username;
  const userType = state.user_session?.user_type;
  const isGallery = userType === 'gallery';
  const location = useLocation();
  const pathname = location?.pathname || '';
  const channelMatch = pathname.startsWith('/channel/')
    ? pathname.match(/^\/channel\/([^/]+)\/([^/]+)/)
    : null;
  const activeOwnerUsername = channelMatch ? decodeURIComponent(channelMatch[1]) : null;
  const activeChannelId = channelMatch ? decodeURIComponent(channelMatch[2]) : null;
  const channelSummaries = state?.channelSummaries || {};
  const hasSummaries = Object.keys(channelSummaries).length > 0;

  const [channels, setChannels] = useState([]);
  const [channelsWithOwner, setChannelsWithOwner] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [hasOwnChannels, setHasOwnChannels] = useState(false);
  const [ownerChannels, setOwnerChannels] = useState([]);
  const memberChannelsSignatureRef = useRef('');
  const listSourceRef = useRef(null);
  const [chatsReloadKey, setChatsReloadKey] = useState(0);
  const hydratedOverviewIdsRef = useRef(new Set());

  useEffect(() => {
    if (location?.state?.fromPath) {
      sessionStorage.setItem('channels_from_path', location.state.fromPath);
    }
  }, [location?.state?.fromPath]);

  const resolveFromPath = useCallback(() => (
    location?.state?.fromPath
    || sessionStorage.getItem('channels_from_path')
    || '/profile'
  ), [location?.state?.fromPath]);

  useEffect(() => {
    if (messagesType !== 'channels') return;
    if (hasSummaries) {
      // When summaries are available, avoid legacy list fetch/update
      setChannels([]);
      setChannelsWithOwner([]);
      setLoading(false);
      setError(null);
      return;
    }

    const list = Array.isArray(memberChannels) ? memberChannels : [];
    const signature = computeChannelSignature(list);
    const shouldUpdateList =
      signature !== memberChannelsSignatureRef.current || listSourceRef.current !== 'channels';

    if (shouldUpdateList) {
      setChannels(list);
      listSourceRef.current = 'channels';
      if (list.length === 0) {
        setChannelsWithOwner([]);
      }
    }

    memberChannelsSignatureRef.current = signature;
    setError(null);
    setLoading(memberLoading && list.length === 0);
  }, [messagesType, memberChannels, memberLoading, hasSummaries]);

  useEffect(() => {
    if (messagesType !== 'chats') return;
    
    let cancelled = false;
    if (listSourceRef.current !== 'chats') {
      setChannels([]);
    }
    listSourceRef.current = 'chats';
    setChannelsWithOwner([]);
    setLoading(true);
    setError(null);
    UserService.getUserChats()
      .then(({ response, data }) => {
        if (cancelled) return;
        if (response.ok) {
          const list = Array.isArray(data?.data) ? data.data : [];
          setChannels(list);
          setError(null);
        } else {
          setError('Failed to load chats.');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error fetching chats:', err);
        setError('Failed to load chats.');
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [messagesType, chatsReloadKey]);

  useEffect(() => {
    if (hasSummaries) {
      setHasOwnChannels(false);
      setOwnerChannels([]);
      return;
    }
    const ownerId = state?.user_session?._id;
    if (!ownerId) {
      setHasOwnChannels(false);
      setOwnerChannels([]);
      return;
    }
    ChannelsService.getByOwner(ownerId)
      .then(({ response, data }) => {
        if (response?.ok) {
          const list = Array.isArray(data?.data) ? data.data : [];
          setHasOwnChannels(list.length > 0);
          setOwnerChannels(list);
        } else {
          setHasOwnChannels(false);
          setOwnerChannels([]);
        }
      })
      .catch(() => { setHasOwnChannels(false); setOwnerChannels([]); });
  }, [state?.user_session?._id, hasSummaries]);

  useEffect(() => {
    if (hasSummaries) return; // owner/name enrichment not needed
    if (messagesType !== 'channels') {
      setChannelsWithOwner([]);
      return;
    }
    if (!channels.length) {
      setChannelsWithOwner([]);
      return;
    }

    let cancelled = false;

    async function fetchOwnerNames() {
      const updatedChannels = await Promise.all(
        channels.map(async (channel) => {
          const ownerUsername = channel?.owner?.user_username || channel?.owner_username;
          const endpoint = ownerUsername
            ? `${config.apis.api.url}/user/public/${ownerUsername}`
            : null;
          try {
            let ownerName = ownerUsername || 'Unknown';
            let ownerImage = null;
            let ownerType = null;
            if (endpoint) {
              const { response, data } = await fetchWrapper.get(endpoint, { injectToken: true });
              if (response.ok && data) {
                if (data.channel_name && data.channel_name.trim() !== '') {
                  ownerName = data.channel_name;
                }
                ownerImage = resolveImageId(data?.user_image);
                ownerType = data.user_type ?? data.__t ?? null;
              }
            }
            const latestMessageTimestamp =
              channel?.latestMessage?.createdAt ||
              channel?.latestMessage?.created_at ||
              channel?.latestMessage?.timestamp ||
              null;
            const lastActivity = latestMessageTimestamp || channel.lastActivityAt || channel.updatedAt || channel.createdAt || null;
            return {
              ...channel,
              ownerName,
              ownerUsername: ownerUsername || null,
              ownerImage,
              ownerType: ownerType ? resolveChannelOwnerProfileType(ownerType, null) : null,
              lastActivity
            };
          } catch (err) {
            console.error('Error fetching profile for channel', channel?._id, err);
            const latestMessageTimestamp =
              channel?.latestMessage?.createdAt ||
              channel?.latestMessage?.created_at ||
              channel?.latestMessage?.timestamp ||
              null;
            const lastActivity = latestMessageTimestamp || channel.lastActivityAt || channel.updatedAt || channel.createdAt || null;
            return {
              ...channel,
              ownerName: ownerUsername || 'Unknown',
              ownerUsername: ownerUsername || null,
              ownerImage: null,
              ownerType: null,
              lastActivity
            };
          }
        })
      );
      if (!cancelled) {
        setChannelsWithOwner(updatedChannels);
      }
    }

    fetchOwnerNames();

    return () => {
      cancelled = true;
    };
  }, [messagesType, channels, hasSummaries]);

  useEffect(() => {
    if (!state.channelUnreadUpdated) return;

    if (messagesType === 'chats') {
      setChatsReloadKey((value) => value + 1);
    }

    setChannelUnreadUpdated(false);
  }, [state.channelUnreadUpdated, messagesType, setChannelUnreadUpdated]);

  // Unified conversation list from summaries (preferred)
  const conversationList = useMemo(() => {
    const values = Object.values(channelSummaries || {});
    const enriched = values.map((entry) => {
      const channelId = String(entry.channelId);
      const ownerId = entry.ownerId || entry?.channel?.ownerId || entry?.channel?.owner?._id || entry?.channel?.owner || null;
      const ownerUsername = entry.ownerUsername || entry?.channel?.owner_username || entry?.channel?.owner?.user_username || null;
      const isOwnChannel = ownerId && String(ownerId) === String(state?.user_session?._id);
      const ownerTypeRaw =
        entry.channelKind
        || entry.channel_kind
        || entry?.channel?.channel_kind
        || entry?.channel?.channelKind
        || entry.ownerType
        || entry?.channel?.owner?.user_type
        || entry?.channel?.owner?.__t
        || (isOwnChannel ? state?.user_session?.user_type : null)
        || null;
      const title = entry.title || entry?.channel?.title || entry?.channel?.channel_name || ownerUsername || 'Unknown';
      const avatar =
        resolveOwnerAvatar(entry)
        || (isOwnChannel ? resolveImageId(state?.user_session?.user_image) : null)
        || null;
      return {
        _id: channelId,
        ownerId: ownerId ? String(ownerId) : null,
        ownerUsername,
        ownerType: ownerTypeRaw ? resolveChannelOwnerProfileType(ownerTypeRaw, null) : null,
        ownerName: title,
        ownerImage: avatar,
        lastActivity: entry.lastActivity || entry?.lastMessage?.createdAt || entry?.lastMessage?.created_at || null,
        title,
        summary: entry,
      };
    });
    enriched.sort((a, b) => new Date(b.lastActivity || 0) - new Date(a.lastActivity || 0));
    return enriched;
  }, [channelSummaries, state?.user_session?._id, state?.user_session?.user_image]);

  useEffect(() => {
    if (messagesType !== 'channels' || !hasSummaries) return;

    const entries = Object.values(channelSummaries || []);
    const targets = entries.filter((entry) => {
      const channelId = normalizeChannelId(entry?.channelId || entry?.channel?._id);
      if (!channelId) return false;
      if (hydratedOverviewIdsRef.current.has(channelId)) return false;

      const ownerUsername = entry?.ownerUsername || entry?.channel?.owner_username || entry?.channel?.owner?.user_username || null;
      const title = entry?.title || entry?.channel?.title || null;
      const avatar = resolveOwnerAvatar(entry);
      const ownerType = entry?.ownerType || entry?.channel?.owner?.user_type || null;

      return !ownerUsername || !title || !avatar || !ownerType;
    });

    if (!targets.length) return;

    let cancelled = false;
    (async () => {
      const patches = await Promise.all(
        targets.map(async (entry) => {
          const channelId = normalizeChannelId(entry?.channelId || entry?.channel?._id);
          if (!channelId) return null;

          hydratedOverviewIdsRef.current.add(channelId);
          try {
            const { response, data } = await ChannelsService.getOverview(channelId);
            if (!response?.ok || !data) return null;

            const owner = data?.owner && typeof data.owner === 'object' ? data.owner : null;
            const ownerId = owner?._id || data?.owner || entry?.ownerId || null;
            const ownerUsername =
              owner?.user_username
              || data?.owner_username
              || entry?.ownerUsername
              || null;
            const ownerType =
              data?.channel_kind
              || data?.channelKind
              || owner?.user_type
              || owner?.__t
              || entry?.ownerType
              || null;
            const avatar =
              resolveImageId(owner?.user_image)
              || resolveOwnerAvatar(entry)
              || null;
            const title =
              data?.title
              || owner?.channel_name
              || ownerUsername
              || entry?.title
              || 'Unknown';

            return {
              channelId,
              patch: {
                ...entry,
                channelId,
                ownerId: ownerId ? String(ownerId) : null,
                ownerUsername,
                ownerType: ownerType ? resolveChannelOwnerProfileType(ownerType, null) : null,
                ownerImage: avatar,
                avatar,
                title,
                channel: data,
              },
            };
          } catch (_) {
            return null;
          }
        })
      );

      if (cancelled) return;

      const validPatches = patches.filter(Boolean);
      if (!validPatches.length) return;

      const handler = stateHandlerRef.current;
      if (!handler) return;
      const current = handler.getValue('channelSummaries') || {};
      const next = { ...current };
      let changed = false;

      validPatches.forEach(({ channelId, patch }) => {
        if (!channelId) return;
        next[channelId] = { ...(next[channelId] || {}), ...patch };
        changed = true;
      });

      if (changed) {
        handler.set('channelSummaries', next);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [messagesType, hasSummaries, channelSummaries]);

  const list = channelsWithOwner.length > 0 ? channelsWithOwner : channels;

  const ownerListEnriched = useMemo(() => {
    const img = resolveImageId(state?.user_session?.user_image);
    const ownerU = profileName || null;
    const ownerName = state?.user_session?.channel_name || ownerU || 'Unknown';
    return (ownerChannels || []).map((ch) => ({
      ...ch,
      ownerUsername: ownerU,
      ownerType: state?.user_session?.user_type
        ? resolveChannelOwnerProfileType(state.user_session.user_type, null)
        : (state?.user_session?.__t ? resolveChannelOwnerProfileType(state.user_session.__t, null) : null),
      ownerName,
      ownerImage: img,
      lastActivity: ch.lastActivityAt || ch.updatedAt || ch.createdAt || null,
    }));
  }, [
    ownerChannels,
    profileName,
    state?.user_session?.channel_name,
    state?.user_session?.user_image,
    state?.user_session?.user_type
  ]);

  const getDisplayTimestamp = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return Utils.getTimeOfMessageFromTimestamp(date.getTime());
  };

  const buildMessagePreview = (message) => {
    if (!message) return '';
    const sender =
      (message.message_sender && typeof message.message_sender === 'object' && message.message_sender) ||
      (message.sender && typeof message.sender === 'object' && message.sender) ||
      (message.user && typeof message.user === 'object' && message.user) ||
      null;

    const senderName =
      sender?.user_username ||
      message?.message_sender_username ||
      message?.sender_username ||
      message?.user_username ||
      '';

    const messageText = message?.message_text || message?.text || '';
    const isSticker = Boolean(message?.message_sticker);
    const baseText = isSticker ? 'Sticker' : messageText.trim();

    if (!baseText && !senderName) return '';
    const combined = senderName ? `${senderName}: ${baseText}` : baseText;
    return combined.length > 80 ? `${combined.slice(0, 77)}...` : combined;
  };

  const resolveChannelSummary = useCallback((channel) => {
    const identifier =
      normalizeChannelId(channel?._id)
      || normalizeChannelId(channel?.id)
      || normalizeChannelId(channel?.channelId)
      || normalizeChannelId(channel?.channel_id)
      || null;
    if (!identifier) return null;
    return channelSummaries[identifier] || null;
  }, [channelSummaries]);

  const getUnreadFlag = (channel, summary) => {
    if (summary && typeof summary.unreadCount === 'number') {
      return summary.unreadCount > 0;
    }
    const rawValue =
      channel?.unreadCount ??
      channel?.unread_count ??
      channel?.unread ??
      channel?.unreadMessages ??
      0;
    if (typeof rawValue === 'number') return rawValue > 0;
    if (typeof rawValue === 'string') {
      const parsed = parseInt(rawValue, 10);
      return !Number.isNaN(parsed) && parsed > 0;
    }
    return false;
  };

  const renderChannelItem = (channel, keyOverride, indexOverride = 0) => {
    const { ownerUsername, ownerName, lastActivity, title } = channel;
    const channelIdRaw =
      channel?._id
      ?? channel?.channelId
      ?? channel?.channel_id
      ?? channel?.id
      ?? null;
    const channelId = normalizeChannelId(channelIdRaw);
    const summary = channel.summary || resolveChannelSummary(channel);
    const latestMessage = summary?.lastMessage
      || channel?.latestMessage
      || channel?.lastMessage
      || channel?.last_message
      || null;

    const lastMessageTimestamp = summary?.lastActivity
      || latestMessage?.createdAt
      || latestMessage?.created_at
      || latestMessage?.timestamp
      || lastActivity;

    const preview = summary?.previewText || buildMessagePreview(latestMessage);
    const hasUnread = getUnreadFlag(channel, summary);

    const summaryOwnerId = summary?.ownerId || summary?.channel?.owner?._id || summary?.channel?.owner || null;
    const channelOwnerId = channel?.ownerId || channel?.owner?._id || channel?.owner || summaryOwnerId || null;
    const isOwnChannel = channelOwnerId && String(channelOwnerId) === String(state?.user_session?._id);
    const resolvedOwnerUsername =
      ownerUsername
      || summary?.ownerUsername
      || summary?.channel?.owner_username
      || summary?.channel?.owner?.user_username
      || (isOwnChannel ? state?.user_session?.user_username : null)
      || (activeChannelId && channelId && String(activeChannelId) === String(channelId) ? activeOwnerUsername : null);
    const resolvedOwnerImage =
      resolveOwnerAvatar(summary)
      || resolveOwnerAvatar(channel)
      || (isOwnChannel ? resolveImageId(state?.user_session?.user_image) : null);
    const resolvedOwnerType = resolveChannelOwnerProfileType(
      summary?.channel?.channel_kind
      || summary?.channel?.channelKind
      || channel?.channel_kind
      || channel?.channelKind
      || channel?.ownerType
      || summary?.ownerType
      || summary?.channel?.owner?.user_type
      || summary?.channel?.owner?.__t
      || channel?.owner?.user_type
      || channel?.owner?.__t
      || (isOwnChannel ? state?.user_session?.user_type : null),
      'collector'
    );
    const resolvedTitle =
      title
      || ownerName
      || summary?.title
      || summary?.channel?.title
      || summary?.channel?.channel_name
      || (isOwnChannel ? state?.user_session?.channel_name : null)
      || resolvedOwnerUsername
      || 'Unknown';

    const to = (messagesType === 'channels' && resolvedOwnerUsername && channelId)
      ? `/channel/${resolvedOwnerUsername}/${channelId}`
      : '#';

    const classes = ['channel-menu__item'];
    if (hasUnread) classes.push('channel-menu__item--unread');

    const matchesActiveChannel =
      messagesType === 'channels'
      && activeChannelId
      && channelId
      && String(channelId) === String(activeChannelId)
      && (!activeOwnerUsername
        || (resolvedOwnerUsername && activeOwnerUsername.toLowerCase() === String(resolvedOwnerUsername).toLowerCase()));

    if (matchesActiveChannel) {
      classes.push('channel-menu__item--active');
    }

    const safeKey = keyOverride || `${messagesType}-${channelId || resolvedOwnerUsername || resolvedTitle || indexOverride}`;

    return (
      <Link
        to={to}
        key={safeKey}
        className={classes.join(' ')}
      >
        <div className="channel-menu__item-avatar">
          <UserProfileImage
            image={resolvedOwnerImage || null}
            typeOfProfile={resolvedOwnerType}
            className="chat-message__user-image"
          />
        </div>
        <div className="channel-menu__item-info">
          <div className="channel-menu__item-header">
            <span className="channel-menu__item-name">
              {resolvedTitle}
            </span>
            <span className="channel-menu__item-timestamp">
              {getDisplayTimestamp(lastMessageTimestamp)}
            </span>
          </div>
          {resolvedOwnerUsername ? (
            <div className="channel-menu__item-creator">
              by {resolvedOwnerUsername}
            </div>
          ) : null}
          <div className="channel-menu__item-subinfo">
            <div className="channel-menu__item-message">
              {preview || 'Start the conversation'}
            </div>
            {hasUnread && <span className="channel-menu__item-unread-dot" />}
          </div>
        </div>
      </Link>
    );
  };

  const joinedChannels = isGallery
    ? []
    : list.filter((channel) => (channel.ownerUsername || '') !== (profileName || ''));
  joinedChannels.sort((a, b) => new Date(b.lastActivity || 0) - new Date(a.lastActivity || 0));

  const recommendedExcludedChannelIds = useMemo(() => {
    const ids = new Set();

    conversationList.forEach((item) => {
      const id = normalizeChannelId(item?._id || item?.channelId || item?.channel_id || item?.id);
      if (id) ids.add(id);
    });

    ownerListEnriched.forEach((item) => {
      const id = normalizeChannelId(item?._id || item?.channelId || item?.channel_id || item?.id);
      if (id) ids.add(id);
    });

    joinedChannels.forEach((item) => {
      const id = normalizeChannelId(item?._id || item?.channelId || item?.channel_id || item?.id);
      if (id) ids.add(id);
    });

    return Array.from(ids);
  }, [conversationList, ownerListEnriched, joinedChannels]);

  const hasAnyJoinedOrOwnedChannels = hasSummaries
    ? conversationList.length > 0
    : ownerListEnriched.length > 0 || joinedChannels.length > 0;

  return (
    <div className="channel-menu__container">
      <div className="channel-menu__header">
        <BackArrowIcon
          className="channel-menu__go-back"
          onClick={() => {
            const from = resolveFromPath();
            if (from) {
              sessionStorage.removeItem('channels_from_path');
              navigate(from, { replace: true });
            } else {
              navigate(-1);
            }
          }}
        />
        <h1 className="channel-menu__title">{profileName}</h1>
      </div>

      <div className="user-profile__community-navigation element-non-selectable">
        <div
          className={`user-profile__community-navigation-link ${messagesType === 'channels' ? 'active' : ''}`}
          onClick={() => {
            setMessagesType('channels');
            const fromPath = resolveFromPath();
            if (fromPath) {
              sessionStorage.setItem('channels_from_path', fromPath);
            }
            navigate('/channels', { state: { fromPath } });
          }}
        >
          <span className="user-profile__community-navigation-link-text">{t('navigation.channels')}</span>
        </div>
        <div
          className={`user-profile__community-navigation-link ${messagesType === 'chats' ? 'active' : ''}`}
          onClick={() => {
            setMessagesType('chats');
            const fromPath = resolveFromPath();
            if (fromPath) {
              sessionStorage.setItem('channels_from_path', fromPath);
            }
            navigate('/chat/dm', { state: { fromPath } });
          }}
        >
          <span className="user-profile__community-navigation-link-text">{t('navigation.chats')}</span>
        </div>
      </div>

      <div className="channel-menu__list">
        {loading ? (
          <div className="channel-menu__loader">
            <Loader inline thin />
          </div>
        ) : (
          <>
            {messagesType === 'channels' && hasSummaries && (
              <div className="channels-joined-section">
                {conversationList.map((item, index) => renderChannelItem(item, `conv-${item?._id || item?.channelId || index}`, index))}
              </div>
            )}
            {messagesType === 'channels' && !hasSummaries && !error && (
              <>
                {hasOwnChannels && ownerListEnriched.length > 0 && (
                  <div className="channels-joined-section">
                    {ownerListEnriched.map((ch, index) => renderChannelItem(ch, `own-${ch?._id || ch?.channelId || index}`, index))}
                  </div>
                )}
                {!isGallery && (
                  <div className="channels-joined-section">
                    {joinedChannels.map((channel, index) => renderChannelItem(channel, null, index))}
                  </div>
                )}
              </>
            )}
            {error && (
              <div className="channel-menu__error">{error}</div>
            )}
            {messagesType === 'channels' && !error && (
              <RecommendedChannelsSection
                excludedChannelIds={recommendedExcludedChannelIds}
                hasJoinedChannels={hasAnyJoinedOrOwnedChannels}
                cacheScopeKey={state?.user_session?._id || state?.user_session?.user_username || 'anonymous'}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
